import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import Layout from '../Layout/Layout';
import EmptyState from '../SharedComponents/Forms/EmptyState';
import FloatingActionButton from '../SharedComponents/FloatingActionButton/FloatingActionButton';
import DeleteConfirmation from '../SharedComponents/DeleteConfirmation';

import Create from './Components/Create';
import UpdateCampaign from './Components/Update';
import AssignPopup from './Components/AssignToDevicePopup';
import CampaignSettings from './Components/setup/component/settings/CampaignSettings';
import UsageAndConfirmation from './Components/UsageAndConfirmation';

import { useTitle, formatDuration } from '../../config/global';
import { useGalleryContext } from '../Contexts/GalleryContext';
import {
  useGetAllCampaigns,
  useUpdateCampaignMutation,
  useCampaignUsage,
} from '../../queries';
import { useDeleteRowMutation } from '../../queries/dataTable';
import {
  setCampaignDetails,
  setIsPrayerCampaign,
  toggleDeleteDialog,
  toggleDialog,
} from '../../redux/Slices/Global/globalSlice';
import { setCampaignPreview } from '../../redux/Slices/campaignSlice';
import { emptyCampaign } from '../../config/constants';

import '../Media/css/mediaGallery.css';
import './css/showsGallery.css';

/**
 * Shows Gallery — 2026 redesign.
 * "Shows" is the user-facing term; the backend table is still `campaign`.
 * Hooks + dialog flags untouched — only the visual layer is new.
 */

// Old S3 buckets the API still references — rewrite to the active CDN host
// so cover thumbnails actually resolve.
const CDN_OLD_1 = 'http://storage-popuppush-copy.s3.eu-west-1.amazonaws.com';
const CDN_OLD_2 = 'http://storage-popuppush.s3.me-south-1.amazonaws.com';
const CDN_NEW   = 'https://storage.popuppush.com';
function normalizeCdn(url) {
  if (!url) return url;
  if (url.includes(CDN_OLD_1)) return url.replace(CDN_OLD_1, CDN_NEW);
  if (url.includes(CDN_OLD_2)) return url.replace(CDN_OLD_2, CDN_NEW);
  return url;
}

export default function ShowsGallery() {
  useTitle('Shows');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { layout, setLayout } = useGalleryContext();

  const { openDialog, deleteDialog } = useSelector((s) => s.global);

  const [searchTerm, setSearchTerm]   = useState('');
  const [sortOrder,  setSortOrder]    = useState('desc');
  const [statusFilter, setStatusFilter] = useState('');  // '', 'SYNCED', 'CHANGES'
  const [currentShow,  setCurrentShow]  = useState({ name: '' });
  const [campaignUsage, setCampaignUsage] = useState();
  const [menuItem, setMenuItem] = useState(null);

  const updateCampaign     = useUpdateCampaignMutation();
  const fetchCampaignUsage = useCampaignUsage();
  const deleteCampaign     = useDeleteRowMutation();

  const sortMenuRef     = useRef(null);
  const filterMenuRef   = useRef(null);
  const rowMenuRef      = useRef(null);
  const scrollRef       = useRef(null);
  const sentinelRef     = useRef(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useGetAllCampaigns({ per_page: 24, search: searchTerm.trim() || undefined });

  // ── Infinite scroll ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!sentinelRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRef.current ?? null, rootMargin: '600px 0px 400px 0px' },
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const shows = useMemo(() => {
    const arr = data?.pages?.flatMap((p) => p.items) ?? [];
    let out = arr;
    /* Filters on the SAME computed value the badge shows. Left on status, a
       "Draft" filter returned 68 of 69 shows — worse than no filter. */
    if (statusFilter === 'CHANGES')    out = out.filter((s) => s.has_unpublished_changes === true);
    else if (statusFilter === 'SYNCED') out = out.filter((s) => s.has_unpublished_changes !== true);
    if (sortOrder === 'asc') out = [...out].reverse();
    return out;
  }, [data, sortOrder, statusFilter]);

  // ── Navigation + dialog plumbing ────────────────────────────────────────
  const goToSetup = (id) => {
    sessionStorage.setItem('is_default_prayer', JSON.stringify(false));
    dispatch(setIsPrayerCampaign(false));
    navigate(`/campaigns/${id}/setup`);
  };
  const previewShow = (item) => {
    dispatch(setCampaignPreview(true));
    goToSetup(item.id);
  };
  const openCreate = () => {
    setCurrentShow(emptyCampaign);
    dispatch(toggleDialog('createCampaign'));
  };
  const openEditCover = (item) => {
    setCurrentShow(item);
    dispatch(setCampaignDetails(item));
    dispatch(toggleDialog('campaignSettings'));
  };
  const openAssign = (item) => {
    setCurrentShow(item);
    dispatch(toggleDialog('assignDeviceGroup'));
  };
  const confirmDelete = (item) => {
    setCurrentShow(item);
    fetchCampaignUsage.mutate({ id: item.id }, {
      onSuccess: (res) => {
        setCampaignUsage(res);
        dispatch(toggleDialog('usageAndConfirmation'));
      },
    });
  };
  const deleteShow = async () => {
    await deleteCampaign.mutateAsync({ id: currentShow?.id, table: 'campaign' }, {
      onSuccess: () => {
        refetch();
        dispatch(toggleDeleteDialog('campaign'));
      },
    });
  };

  // ── ⋮ menu ──────────────────────────────────────────────────────────────
  const openRowMenu = (e, item) => {
    setMenuItem(item);
    rowMenuRef.current?.toggle(e);
  };
  const rowMenuModel = useMemo(() => {
    if (!menuItem) return [];
    const isDefault = menuItem.name === 'default';
    const isEmpty   = menuItem.campaign_duration === 0;
    const items = [
      { label: t('shows.preview',     { defaultValue: 'Preview' }),          icon: 'pi pi-eye',         command: () => previewShow(menuItem) },
      { label: t('shows.edit_setup',  { defaultValue: 'Edit Show' }),        icon: 'pi pi-pencil',      command: () => goToSetup(menuItem.id) },
      { label: t('shows.edit_cover',  { defaultValue: 'Edit Cover' }),       icon: 'pi pi-image',       command: () => openEditCover(menuItem) },
      { label: t('shows.assign',      { defaultValue: 'Assign to Device' }), icon: 'pi pi-arrow-up',    disabled: isEmpty, command: () => !isEmpty && openAssign(menuItem) },
    ];
    if (!isDefault) {
      items.push({ separator: true });
      items.push({ label: t('buttons.delete'), icon: 'pi pi-trash', command: () => confirmDelete(menuItem) });
    }
    return items;
  }, [menuItem, t]);

  // ── Sort + Filter popup models ─────────────────────────────────────────
  const sortMenuItems = [
    { label: t('media.sort_recent', { defaultValue: 'Recent first' }), icon: sortOrder === 'desc' ? 'pi pi-check' : 'pi pi-circle', command: () => setSortOrder('desc') },
    { label: t('media.sort_old',    { defaultValue: 'Oldest first' }), icon: sortOrder === 'asc'  ? 'pi pi-check' : 'pi pi-circle', command: () => setSortOrder('asc') },
  ];
  const filterMenuItems = [
    { label: t('shows.all_statuses', { defaultValue: 'All statuses' }), icon: !statusFilter ? 'pi pi-check' : 'pi pi-circle', command: () => setStatusFilter('') },
    { label: t('shows.published',           { defaultValue: 'Published' }),           icon: statusFilter === 'SYNCED'  ? 'pi pi-check' : 'pi pi-circle', command: () => setStatusFilter('SYNCED') },
    { label: t('shows.unpublished_changes', { defaultValue: 'Unpublished changes' }), icon: statusFilter === 'CHANGES' ? 'pi pi-check' : 'pi pi-circle', command: () => setStatusFilter('CHANGES') },
  ];

  // ── MobileBar ───────────────────────────────────────────────────────────
  const MobileBar = (
    <div className="pp-mg-mobile-bar d-md-none">
      <div className="pp-mg-mb-search">
        <i className="pi pi-search" />
        <input
          type="search"
          placeholder={t('shows.search_placeholder', { defaultValue: 'Search shows…' })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="pp-mg-mb-actions">
        <button className="pp-mg-mb-upload" onClick={openCreate}>
          <i className="pi pi-plus" />
          <span className="pp-mg-mb-upload-label">{t('shows.new', { defaultValue: 'New' })}</span>
        </button>
        <button className="pp-mg-mb-icon" onClick={(e) => sortMenuRef.current?.toggle(e)} aria-haspopup aria-label="Sort" title="Sort">
          <i className="pi pi-sort-alt" />
        </button>
        <button className={`pp-mg-mb-icon ${statusFilter ? 'is-active' : ''}`} onClick={(e) => filterMenuRef.current?.toggle(e)} aria-haspopup aria-label="Filter" title="Filter">
          <i className="pi pi-filter" />
        </button>
        <div className="pp-view-toggle pp-mg-mb-view" role="group" aria-label="View mode">
          <button type="button" className={layout === 'grid' ? 'is-active' : ''} onClick={() => setLayout('grid')} title="Grid"><i className="pi pi-th-large" /></button>
          <button type="button" className={layout === 'list' ? 'is-active' : ''} onClick={() => setLayout('list')} title="List"><i className="pi pi-list" /></button>
        </div>
      </div>
      <Menu model={sortMenuItems}   popup ref={sortMenuRef}   appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
      <Menu model={filterMenuItems} popup ref={filterMenuRef} appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
    </div>
  );

  // ── Desktop bars ───────────────────────────────────────────────────────
  const TopBar = (
    <div className="pp-media-topbar d-none d-md-flex">
      <div className="pp-media-search">
        <i className="pi pi-search" />
        <input
          type="search"
          placeholder={t('shows.search_placeholder', { defaultValue: 'Search shows…' })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="pp-media-topbar-actions">
        <button className="pp-media-upload-btn" onClick={openCreate}>
          <i className="pi pi-plus" /> {t('shows.new_show', { defaultValue: 'New Show' })}
        </button>
      </div>
    </div>
  );
  const Toolbar = (
    <div className="pp-media-toolbar d-none d-md-flex">
      <button className="pp-media-tool-btn" onClick={(e) => sortMenuRef.current?.toggle(e)}>
        <i className="pi pi-sort-alt" /> {sortOrder === 'desc' ? t('media.sort_recent', { defaultValue: 'Recent first' }) : t('media.sort_old', { defaultValue: 'Oldest first' })}
      </button>
      <button className={`pp-media-tool-btn ${statusFilter ? 'is-active' : ''}`} onClick={(e) => filterMenuRef.current?.toggle(e)}>
        <i className="pi pi-filter" /> {statusFilter || t('shows.all_statuses', { defaultValue: 'All statuses' })}
      </button>
      <div className="pp-media-toolbar-spacer" />
      <div className="pp-view-toggle" role="group" aria-label="View mode">
        <button type="button" className={layout === 'grid' ? 'is-active' : ''} onClick={() => setLayout('grid')} title="Grid view"><i className="pi pi-th-large" /></button>
        <button type="button" className={layout === 'list' ? 'is-active' : ''} onClick={() => setLayout('list')} title="List view"><i className="pi pi-list" /></button>
      </div>
    </div>
  );

  return (
    <Layout padding={false}>
      <div className="pp-media-page">
        {MobileBar}
        {TopBar}
        {Toolbar}

        <div className="pp-media-body pp-fab-safe-bottom" ref={scrollRef}>
          {isLoading && shows.length === 0 ? (
            <div className="pp-media-loading">
              <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" />
            </div>
          ) : shows.length === 0 ? (
            <EmptyState
              icon="pi-desktop"
              title={t('shows.empty_title', { defaultValue: 'No shows yet' })}
              description={t('shows.empty_desc', { defaultValue: 'Combine media into a show and publish it to a screen.' })}
              cta={{ icon: 'pi pi-plus', label: t('shows.new_show', { defaultValue: 'New Show' }), onClick: openCreate }}
            />
          ) : layout === 'grid' ? (
            <ShowsGrid items={shows} onActivate={(it) => goToSetup(it.id)} onMenu={openRowMenu} onPreview={previewShow} t={t} />
          ) : (
            <ShowsList items={shows} onActivate={(it) => goToSetup(it.id)} onMenu={openRowMenu} onPreview={previewShow} t={t} />
          )}
          <div ref={sentinelRef} style={{ height: 1 }} />
        </div>
      </div>

      <Menu model={rowMenuModel} popup ref={rowMenuRef} className="pp-media-row-menu" appendTo={typeof document !== 'undefined' ? document.body : null} />

      {/* Existing dialogs reused */}
      {openDialog?.createCampaign && <Create />}
      {openDialog?.updateCampaign && <UpdateCampaign setReload={refetch} campaign={currentShow} />}
      {openDialog?.assignDeviceGroup && <AssignPopup campaignId={currentShow?.id} campaignName={currentShow?.name} />}
      {openDialog?.campaignSettings && <CampaignSettings showOption={false} campaignId={currentShow?.id} />}
      {openDialog?.usageAndConfirmation && <UsageAndConfirmation campaignUsage={campaignUsage} campaign={currentShow} />}
      {deleteDialog?.campaign && (
        <DeleteConfirmation
          deleteHandler={deleteShow}
          itemName={currentShow?.name}
          dialogKey="campaign"
        />
      )}

      <FloatingActionButton
        icon="pi pi-plus"
        label={t('shows.new_show', { defaultValue: 'New Show' })}
        onClick={openCreate}
      />
    </Layout>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Grid view
 * ─────────────────────────────────────────────────────────────────── */
function ShowsGrid({ items, onActivate, onMenu, onPreview, t }) {
  return (
    <div className="pp-media-grid pp-shows-grid">
      {items.map((item) => <ShowGridCard key={item.id} item={item} onActivate={onActivate} onMenu={onMenu} onPreview={onPreview} t={t} />)}
    </div>
  );
}

function getCoverThumb(item) {
  const raw = item?.cover?.image_thumb ?? item?.cover?.image_path ?? item?.first_media?.thumb ?? null;
  if (!raw || raw === 'loading') return null;
  return normalizeCdn(raw);
}

function ShowGridCard({ item, onActivate, onMenu, onPreview, t }) {
  /* ⚠️ THE COMPUTED DIFFERENCE, NOT item.status. status said "Draft" about 65
     of 69 shows that were fully live, because the wizard writes published rows
     and never touches the flag. What a café wants to know is whether the screen
     matches what they are looking at, and only a comparison answers that —
     has_unpublished_changes is computed server-side and follows playlist
     references, where every real difference on dev actually lives. */
  const hasChanges = item.has_unpublished_changes === true;
  const thumb    = getCoverThumb(item);
  const coverIsColor = item.cover?.type === 'COLOR';
  const coverColor   = item.cover?.color;

  return (
    <div className="pp-media-card pp-show-card" onClick={() => onActivate(item)}>
      <div className="pp-media-thumb pp-show-thumb">
        {thumb ? (
          <LazyLoadImage
            src={thumb}
            alt={item.name}
            className="pp-media-thumb-img"
            onError={(e) => { e.target.onerror = null; e.target.src =
              'data:image/svg+xml;charset=utf-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800"><rect width="100%" height="100%" fill="#1e293b"/></svg>');
            }}
          />
        ) : coverIsColor && coverColor ? (
          <div className="pp-show-cover-color" style={{ background: coverColor }}>
            <span className="pp-show-cover-letter">{(item.name || '?').charAt(0).toUpperCase()}</span>
          </div>
        ) : (
          <div className="pp-show-cover-gradient">
            <span className="pp-show-cover-letter">{(item.name || '?').charAt(0).toUpperCase()}</span>
          </div>
        )}
        <button
          type="button"
          className="pp-media-card-play pp-show-card-preview"
          onClick={(e) => { e.stopPropagation(); onPreview(item); }}
          title={t('shows.preview', { defaultValue: 'Preview' })}
          aria-label={t('shows.preview', { defaultValue: 'Preview' })}
        ><i className="pi pi-eye" /></button>
        <button
          type="button"
          className="pp-media-card-menu"
          onClick={(e) => { e.stopPropagation(); onMenu(e, item); }}
          aria-label="More" title="More"
        ><i className="pi pi-ellipsis-v" /></button>
      </div>
      <div className="pp-media-info">
        <div className="pp-media-name" title={item.name}>{item.name}</div>
        <div className="pp-media-meta">
          {hasChanges
            ? <span className="pp-pill pp-pill-warning"><span className="pp-pill-dot" />{t('shows.unpublished_changes', { defaultValue: 'Unpublished changes' })}</span>
            : <span className="pp-pill pp-pill-success"><span className="pp-pill-dot" />{t('shows.published', { defaultValue: 'Published' })}</span>}
          <span>{formatDuration(item.campaign_duration)}</span>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * List view
 * ─────────────────────────────────────────────────────────────────── */
function ShowsList({ items, onActivate, onMenu, onPreview, t }) {
  return (
    <div className="pp-media-list-wrap">
      <table className="pp-media-list">
        <thead>
          <tr>
            <th className="pp-media-col-thumb"></th>
            <th className="pp-media-col-name">{t('media.name', { defaultValue: 'Name' })}</th>
            <th className="pp-media-col-type d-none d-sm-table-cell">{t('media.status', { defaultValue: 'Status' })}</th>
            <th className="pp-media-col-duration d-none d-md-table-cell">{t('media.duration', { defaultValue: 'Duration' })}</th>
            <th className="pp-media-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => <ShowRow key={item.id} item={item} onActivate={onActivate} onMenu={onMenu} onPreview={onPreview} t={t} />)}
        </tbody>
      </table>
    </div>
  );
}

function ShowRow({ item, onActivate, onMenu, onPreview, t }) {
  const hasChanges = item.has_unpublished_changes === true;   // see the card above
  const thumb   = getCoverThumb(item);
  const coverIsColor = item.cover?.type === 'COLOR';
  const coverColor   = item.cover?.color;

  return (
    <tr className="pp-media-row">
      <td className="pp-media-col-thumb">
        <div className="pp-media-list-thumb pp-show-list-thumb" onClick={() => onActivate(item)}>
          {thumb ? (
            <LazyLoadImage src={thumb} alt={item.name} className="pp-media-list-thumb-img" />
          ) : coverIsColor && coverColor ? (
            <div className="pp-show-cover-color" style={{ background: coverColor }}>
              <span className="pp-show-cover-letter">{(item.name || '?').charAt(0).toUpperCase()}</span>
            </div>
          ) : (
            <div className="pp-show-cover-gradient">
              <span className="pp-show-cover-letter">{(item.name || '?').charAt(0).toUpperCase()}</span>
            </div>
          )}
        </div>
      </td>
      <td className="pp-media-col-name" onClick={() => onActivate(item)}>
        <span className="pp-media-row-name" title={item.name}>{item.name}</span>
        <span className="pp-media-row-sub d-md-none">
          {hasChanges ? t('shows.unpublished_changes', { defaultValue: 'Unpublished changes' }) : t('shows.published', { defaultValue: 'Published' })}
          {' · '}{formatDuration(item.campaign_duration)}
        </span>
      </td>
      <td className="pp-media-col-type d-none d-sm-table-cell">
        {hasChanges
          ? <span className="pp-pill pp-pill-warning"><span className="pp-pill-dot" />{t('shows.unpublished_changes', { defaultValue: 'Unpublished changes' })}</span>
          : <span className="pp-pill pp-pill-success"><span className="pp-pill-dot" />{t('shows.published', { defaultValue: 'Published' })}</span>}
      </td>
      <td className="pp-media-col-duration d-none d-md-table-cell">{formatDuration(item.campaign_duration)}</td>
      <td className="pp-media-col-actions">
        <div className="pp-media-row-actions">
          <button className="pp-media-row-action pp-media-row-primary" onClick={(e) => { e.stopPropagation(); onPreview(item); }} title={t('shows.preview', { defaultValue: 'Preview' })} aria-label={t('shows.preview', { defaultValue: 'Preview' })}>
            <i className="pi pi-eye" />
          </button>
          <button className="pp-media-row-action" onClick={(e) => onMenu(e, item)} title="More" aria-label="More">
            <i className="pi pi-ellipsis-v" />
          </button>
        </div>
      </td>
    </tr>
  );
}
